import {
  isApprovalFlowsEnabled,
  isApprovalFlowsForOrganizerTopicsEnabled,
} from '@/services/systemConfiguration.utilities';
import { SeeqNames } from '@seeqdev/constants';

/** The workbook store's per-type flags. */
export type WorkbookTypeFlags = { isAnalysis: boolean; isTopic: boolean };

export type ApprovalFlowItemTypeConfig = {
  /** The item type as the server reports it, e.g. a Version History's `versionedItemType`. */
  itemType: string;
  /** Translation key of the singular, capitalized type name, e.g. "Analysis". */
  labelKey: string;
  /** Whether the open workbook is this type. */
  matchesWorkbook: (flags: WorkbookTypeFlags) => boolean;
  /**
   * Whether the feature flags turn Approval Flows on for this type. Every type checks `Features/ApprovalFlows/Enabled`,
   * so none is assumed to be on. Evaluated at call time because it reads server configuration, so it must not be
   * captured as a value at module load.
   */
  isEnabled: () => boolean;
};

/**
 * Every item type that Approval Flows can apply to, and the single place that decides which ones are on. The admin
 * copy lists these types by name through `useApprovalFlowItemTypesLabel`, so its strings never mention a type.
 *
 * To extend Approval Flows to a new item type:
 * 1. Add an entry here.
 * 2. Add its singular name to `APPROVAL_FLOWS.ITEM_TYPES` in `en.json` if it is not already there.
 * 3. Add it to `ApprovalFlowSupportedItemTypes.kt` on the server, which enforces the same list.
 *
 * Nothing in the admin strings needs to change.
 */
export const APPROVAL_FLOW_ITEM_TYPES: readonly ApprovalFlowItemTypeConfig[] = [
  {
    itemType: SeeqNames.Types.Analysis,
    labelKey: 'APPROVAL_FLOWS.ITEM_TYPES.ANALYSIS',
    matchesWorkbook: ({ isAnalysis }) => isAnalysis,
    isEnabled: () => isApprovalFlowsEnabled() === true,
  },
  {
    itemType: SeeqNames.Types.Topic,
    labelKey: 'APPROVAL_FLOWS.ITEM_TYPES.TOPIC',
    matchesWorkbook: ({ isTopic }) => isTopic,
    isEnabled: () => isApprovalFlowsEnabled() === true && isApprovalFlowsForOrganizerTopicsEnabled(),
  },
];
